/* File: src/components/ui/SidebarLayout.js */
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Outlet, useLocation, Link, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faHouse, faChartSimple, faUserGroup, faBook, faUser, faSliders,
    faBars, faTimes, faUserCog, faSignOutAlt, faBell,
    faChevronLeft, faChevronRight, faClipboardList,
    faDownload, // Keep install icon
    faCommentDots,
    faFlask, // <-- Icon FOR PLAYGROUND (used for Experimental)
    faPuzzlePiece // <-- NEW ICON FOR CUSTOM GAME
} from '@fortawesome/free-solid-svg-icons';
import { getAuth, signOut } from 'firebase/auth';
import { useNotifications } from '../../contexts/NotificationContext'; // Context for notification data
import NotificationTray from './NotificationTray'; // The component displaying notifications
import { useProfile } from '../../contexts/ProfileContext'; // Context for user and profile info
import FeedbackButton from '../feedback/FeedbackButton'; // Floating feedback button component
// *** Import PWA Context Hook and Utilities ***
import { usePWAInstall } from '../../contexts/PWAInstallContext';
import { isAndroid, isPotentiallyIOSWebApp, isStandaloneMode } from '../../utils/pwaUtils';
import { useSettings } from '../../contexts/SettingsContext'; // For Experimental feature toggle
// *** Import iOS Install Modal ***
import AddToHomeScreenModal from './AddToHomeScreenModal';
// *** IMPORT Emulator Flag ***
import { isEmulatorEnv } from '../../firebaseConfig';


// Helper function to parse CSS pixel values
// ... (keep parsePixels function) ...
const parsePixels = (value) => {
    if (typeof value === 'string' && value.endsWith('px')) {
        return parseFloat(value.replace('px', '')) || 0;
    }
    return 0;
};

// **** RENAMED: Define the target email for EXPERIMENTAL features access ****
const TARGET_EMAIL_FOR_EXPERIMENTAL = "allen@dualnback.com";

const SidebarLayout = () => {
    // ... (keep existing state variables: isMobileMenuOpen, etc.) ...
    const location = useLocation();
    const navigate = useNavigate();
    const auth = getAuth();
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [isSidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [isNotificationTrayOpen, setIsNotificationTrayOpen] = useState(false);
    const [trayTopPosition, setTrayTopPosition] = useState(null);
    const [safeAreaTopPx, setSafeAreaTopPx] = useState(0);
    const [isIosStandalone, setIsIosStandalone] = useState(false);
    const [showIosInstallModal, setShowIosInstallModal] = useState(false);
    const notificationBellRef = useRef(null);
    const notificationTrayRef = useRef(null);
    const mobileNotificationBellRef = useRef(null);
    const { authUser, activeProfile } = useProfile(); // Get authUser here
    // *** Consume PWA Install Context ***
    const { settings } = useSettings(); // Get settings for experimental feature toggle
    const { deferredInstallPrompt, handleInstallClick: contextHandleInstallClick } = usePWAInstall();

    // ... (keep existing context access, effects, callbacks: handleSidebarInstallClick, handleCloseIosModal, etc.) ...
      // --- Safely Access Notification Context ---
    let unreadCount = 0;
    let notificationsLoading = true;
    try {
        const notificationContext = useNotifications();
        if (notificationContext) {
            unreadCount = notificationContext.unreadCount || 0;
            notificationsLoading = typeof notificationContext.isLoading === 'boolean' ? notificationContext.isLoading : true;
        }
    } catch (error) {
        // console.warn("Notification context not available yet:", error.message);
    }


    // --- Effect to Detect iOS Standalone and Get Safe Area ---
    // (Keep this effect exactly as it was)
    useEffect(() => {
        if (typeof window !== 'undefined' && typeof navigator !== 'undefined') {
            const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
            const standalone = 'standalone' in navigator && navigator.standalone;
            const isStandaloneModeValue = isIOS && standalone; // Use local variable for clarity
            setIsIosStandalone(isStandaloneModeValue);
            if (isStandaloneModeValue) {
                if (window.CSS && CSS.supports('top', 'env(safe-area-inset-top)')) {
                    const tempEl = document.createElement('div');
                    tempEl.style.position = 'fixed';
                    tempEl.style.setProperty('--temp-safe-area-top', 'env(safe-area-inset-top)');
                    tempEl.style.visibility = 'hidden'; tempEl.style.height = '0'; tempEl.style.width = '0';
                    document.body.appendChild(tempEl);
                    const safeAreaValue = getComputedStyle(tempEl).getPropertyValue('--temp-safe-area-top');
                    document.body.removeChild(tempEl);
                    const topPx = parsePixels(safeAreaValue);
                    console.log(`iOS Standalone Detected. Safe Area Top Raw: '${safeAreaValue}', Parsed Px: ${topPx}`);
                    setSafeAreaTopPx(topPx);
                } else {
                    console.warn('iOS Standalone Detected, but env(safe-area-inset-top) not supported or measurable.');
                }
            }
        }
    }, []);


    // --- Callback Functions (Memoized for performance) ---

    // Determines if the sidebar should be shown based on the current route
    const shouldShowSidebar = useCallback(() => {
        const noSidebarRoutes = [
            '/game', '/profile-setup', '/challenge-start', '/welcome-setup',
            '/share/replay', '/replay', '/experimental/audio-n-back', // Audio N-Back game is full screen
            '/custom-game/play' // Add custom game player screen to no-sidebar routes
        ];
        return !noSidebarRoutes.some(route => location.pathname.startsWith(route));
    }, [location.pathname]);

    // Checks if a navigation link path is the currently active route
    const isActive = useCallback((path) => {
        if (path === '/dashboard' && location.pathname === '/') return true;
        // For /custom-game, we want to match /custom-game/setup but not /custom-game/play
        if (path === '/custom-game/setup') {
            return location.pathname === path; // Exact match for setup
        }
        return location.pathname === path || location.pathname.startsWith(path + '/');
    }, [location.pathname]);

    // Handles clicks on navigation links, navigates, and closes mobile menu/notifications
    const handleLinkClick = useCallback((path) => {
        navigate(path);
        setIsMobileMenuOpen(false);
        setIsNotificationTrayOpen(false);
    }, [navigate]);

    // Toggles the visibility of the mobile menu
    const toggleMobileMenu = useCallback(() => {
        setIsMobileMenuOpen(prev => !prev);
        setIsNotificationTrayOpen(false);
    }, []);

    // Toggles the collapsed state of the desktop sidebar
    const toggleSidebar = useCallback(() => {
        setSidebarCollapsed(prev => !prev);
        setIsNotificationTrayOpen(false);
    }, []);

    // Toggles the visibility of the notification tray
    const toggleNotificationTray = useCallback((event) => {
        console.log("SidebarLayout: toggleNotificationTray CALLED.");
        event?.stopPropagation();
        const currentBellRef = window.innerWidth < 1024 ? mobileNotificationBellRef : notificationBellRef;
        if (!isNotificationTrayOpen) {
            const buttonRect = currentBellRef.current?.getBoundingClientRect();
            const extraHeaderPadding = 4;
            const headerBaseHeightPx = 4 * 16;
            let desiredTop;
            if (buttonRect) {
                if (window.innerWidth < 1024 && isIosStandalone) {
                    desiredTop = headerBaseHeightPx + safeAreaTopPx + extraHeaderPadding;
                    console.log(`SidebarLayout [Mobile Standalone]: Calculated tray top: ${desiredTop}px`);
                } else if (window.innerWidth < 1024) {
                    desiredTop = headerBaseHeightPx;
                    console.log(`SidebarLayout [Mobile Browser]: Calculated tray top: ${desiredTop}px`);
                } else {
                    desiredTop = buttonRect.top;
                    console.log(`SidebarLayout [Desktop]: Calculated tray top: ${desiredTop}px`);
                }
                setTrayTopPosition(desiredTop);
            } else {
                 console.warn("SidebarLayout: Could not get button rect to position tray. Falling back.");
                 const fallbackTopValue = window.innerWidth < 1024 && isIosStandalone
                    ? `calc(4rem + ${safeAreaTopPx + extraHeaderPadding}px)`
                    : '4rem';
                 setTrayTopPosition(fallbackTopValue);
                 console.log(`SidebarLayout: Using fallback top: ${fallbackTopValue}`);
            }
        }
        setIsNotificationTrayOpen(prev => !prev);
    }, [isNotificationTrayOpen, safeAreaTopPx, isIosStandalone]);


    // Handles user logout
    const handleLogout = useCallback(async () => {
        setIsMobileMenuOpen(false);
        setIsNotificationTrayOpen(false);
        try {
            await signOut(auth);
            navigate('/login', { replace: true });
        } catch (error) {
            console.error("Logout failed:", error);
        }
    }, [auth, navigate]);

    // *** Handle Install Button Click in Sidebar ***
    const handleSidebarInstallClick = useCallback(() => {
        if (isPotentiallyIOSWebApp()) {
            console.log('[Sidebar Install] iOS detected, showing instructions modal.');
            setShowIosInstallModal(true); // Show the iOS instructions
            setIsMobileMenuOpen(false); // Close mobile menu if open
        } else if (isAndroid() && deferredInstallPrompt) {
            console.log('[Sidebar Install] Android detected with prompt, triggering install.');
            contextHandleInstallClick(); // Trigger install prompt via context
            setIsMobileMenuOpen(false); // Close mobile menu if open
        } else {
            console.log('[Sidebar Install] No install action available for this platform/state.');
            // Optionally show a message or do nothing
        }
    }, [deferredInstallPrompt, contextHandleInstallClick]); // Dependencies


    // *** Handle closing the iOS modal ***
    const handleCloseIosModal = useCallback(() => {
        setShowIosInstallModal(false);
    }, []);


    // --- Effects --- (Keep existing effects for outside click and resize)
    // Effect to handle clicks outside the notification tray to close it
     useEffect(() => {
        const handleClickOutside = (event) => {
            if (!event.target) return;
            const isClickOnDesktopBell = notificationBellRef.current?.contains(event.target);
            const isClickOnMobileBell = mobileNotificationBellRef.current?.contains(event.target);
            const isClickInsideTray = notificationTrayRef.current?.contains(event.target);

            if (isNotificationTrayOpen) {
                if (!isClickOnDesktopBell && !isClickInsideTray && !isClickOnMobileBell) {
                    setIsNotificationTrayOpen(false);
                }
            }
        };
        if (isNotificationTrayOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isNotificationTrayOpen]);

    // Effect to close the mobile menu if the window resizes to a larger screen
    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth >= 1024 && isMobileMenuOpen) {
                setIsMobileMenuOpen(false);
            }
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [isMobileMenuOpen]);

    // --- Reusable UI Sub-Components --- (Keep Logo, ProfileSection, NavLink as they are)
    // ... (Logo component definition) ...
     const Logo = ({ collapsed = false }) => (
        <Link
            to="/dashboard" // Link to the main dashboard
            className={`flex items-center text-white flex-shrink-0 ${collapsed ? 'justify-center' : ''} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-800 focus:ring-indigo-500 rounded-md`}
            title="Dual N-Back Training Home"
        >
            {/* N² Icon */}
            <div className="h-9 w-9 bg-indigo-600 rounded-lg flex items-center justify-center text-white flex-shrink-0 shadow-sm select-none">
                <span className="font-tomorrow font-semibold text-lg">N²</span>
            </div>
            {/* Text Label (only when not collapsed) */}
            {!collapsed && (
                <div className="flex flex-col ml-3">
                    <div className="flex items-center">
                        <span className="font-bold text-lg whitespace-nowrap">Dual N-Back</span>
                        {/* Beta Badge */}
                        <span className="ml-1.5 px-1 py-0.5 bg-gray-900 text-indigo-300 text-xs font-semibold rounded-sm tracking-wider select-none" style={{ fontSize: "0.6rem", letterSpacing: "0.05em" }}>BETA</span>
                    </div>
                </div>
            )}
        </Link>
    );
    // ... (ProfileSection component definition) ...
     const ProfileSection = ({ collapsed = false }) => (
        // Only render if the user is authenticated
        authUser && (
            <div className={`px-3 py-3 border-t border-gray-700 flex items-center ${collapsed ? 'justify-center' : ''} flex-shrink-0`}>
                 {/* Profile Avatar/Initial */}
                 <div className={`h-9 w-9 rounded-full flex items-center justify-center text-white shadow-sm flex-shrink-0 ${collapsed ? 'bg-indigo-500' : 'bg-indigo-600'} select-none`}>
                    {authUser.photoURL ? (
                        <img src={authUser.photoURL} alt="Profile" className="h-full w-full rounded-full object-cover" />
                    ) : (
                        // Fallback to initials if no photoURL
                        <span className="font-medium">
                            {activeProfile?.username ? activeProfile.username.charAt(0).toUpperCase() :
                             (authUser.displayName ? authUser.displayName.charAt(0).toUpperCase() :
                              (authUser.email ? authUser.email.charAt(0).toUpperCase() : "U"))}
                        </span>
                    )}
                </div>
                {/* Profile Name and Email (only when not collapsed) */}
                {!collapsed && (
                    <div className="ml-3 overflow-hidden">
                        {activeProfile?.username && (
                            <p className="text-sm font-medium text-white truncate flex items-center" title={`Active Profile: ${activeProfile.username}`}>
                                <span className="w-2 h-2 bg-green-400 rounded-full mr-1.5 flex-shrink-0" aria-hidden="true"></span>
                                {activeProfile.username}
                            </p>
                        )}
                        <p className={`text-xs text-gray-400 truncate ${activeProfile?.username ? '' : 'text-sm font-medium'}`} title={authUser.email || 'User Email'}>{authUser.email || 'No email provided'}</p>
                    </div>
                )}
            </div>
        )
    );
    // ... (NavLink component definition) ...
    const NavLink = ({ path, icon, label, collapsed = false }) => (
        <button
            onClick={() => handleLinkClick(path)}
            className={`flex items-center w-full text-left px-3 py-2.5 rounded-md text-sm font-medium transition-colors duration-150 ease-in-out group focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500 ${
                isActive(path)
                    ? 'bg-indigo-600 text-white font-semibold shadow-inner cursor-default' // Active state
                    : 'text-gray-300 hover:bg-gray-700 hover:text-white' // Inactive state
            } ${collapsed ? 'justify-center' : ''}`}
            disabled={isActive(path)} // Prevent clicking if already on the page
            aria-current={isActive(path) ? 'page' : undefined} // Accessibility: current page
            title={collapsed ? label : undefined} // Show tooltip when collapsed
        >
            <FontAwesomeIcon icon={icon} className={`${collapsed ? '' : 'mr-3'} h-5 w-5 flex-shrink-0 text-gray-400 group-hover:text-white ${isActive(path) ? 'text-white' : ''} transition-colors duration-150 ease-in-out`} aria-hidden="true" />
            {!collapsed && <span className="whitespace-nowrap">{label}</span>}
        </button>
    );


    // --- Calculate Dynamic Styles ---
    const applySafeArea = isIosStandalone && safeAreaTopPx > 0;
    const headerBaseHeightPx = 4 * 16;
    const extraHeaderPadding = 4;
    const mobileHeaderPaddingTop = applySafeArea ? safeAreaTopPx + extraHeaderPadding : 0;
    const mobileHeaderMinHeight = applySafeArea ? headerBaseHeightPx + mobileHeaderPaddingTop : headerBaseHeightPx;
    const mobileHeaderStyle = applySafeArea
        ? { minHeight: `${mobileHeaderMinHeight}px`, paddingTop: `${mobileHeaderPaddingTop}px`, boxSizing: 'border-box' }
        : {};
    const mainContentPaddingTop = applySafeArea ? `${mobileHeaderMinHeight}px` : undefined;
    const mainContentStyle = mainContentPaddingTop ? { paddingTop: mainContentPaddingTop } : {};
    const mobileMenuStyle = applySafeArea ? { paddingTop: `${safeAreaTopPx}px` } : {};
    const notificationTrayTop = applySafeArea ? `${mobileHeaderMinHeight}px` : undefined;
    const mobileNotificationStyle = notificationTrayTop
        ? { top: typeof trayTopPosition === 'number' ? `${trayTopPosition}px` : notificationTrayTop }
        : { top: typeof trayTopPosition === 'number' ? `${trayTopPosition}px` : '4rem' };

    // *** Determine if Install button should be shown - CALCULATED HERE ***
    const isCurrentlyStandalone = isStandaloneMode();
    // *** Use deferredInstallPrompt directly from context here ***
    const canShowInstallOption = !isCurrentlyStandalone && (isPotentiallyIOSWebApp() || (isAndroid() && deferredInstallPrompt));
    const installButtonLabel = isPotentiallyIOSWebApp() ? "Add to Home" : "Install App";
    const installButtonIcon = faDownload;

    // **** CALCULATE Experimental Link Visibility ****
    // Combines the new setting with the existing email check
    const showExperimentalFeaturesLink = settings?.enableExperimentalFeatures && authUser && authUser.email?.toLowerCase() === TARGET_EMAIL_FOR_EXPERIMENTAL.toLowerCase();


    // --- Main Component Render ---

    // If sidebar should not be shown, handle safe area padding
    if (!shouldShowSidebar()) {
         // ... (no-sidebar return block remains the same) ...
          return (
            <div className="flex-1 flex flex-col min-h-screen bg-gray-100 overflow-y-auto">
                 <main
                    className="flex-1"
                    style={ applySafeArea ? { paddingTop: `${safeAreaTopPx}px`, paddingBottom: `env(safe-area-inset-bottom)` } : {}}
                >
                    <Outlet />
                 </main>
                 {authUser && <FeedbackButton />}
            </div>
        );
    }

    // Otherwise, render the full layout with Sidebar, Headers, etc.
    return (
        // Keep overflow-hidden on the outermost container
        <div className="flex h-screen bg-gray-100"> {/* Main container */}

            {/* === Desktop Sidebar === */}
            <aside
                className={`hidden lg:flex flex-col bg-gray-800 text-white shadow-lg z-30 flex-shrink-0 transition-all duration-300 ease-in-out ${
                    isSidebarCollapsed ? 'w-16' : 'w-64'
                }`}
                aria-label="Main Navigation"
            >
                 {/* Sidebar Header */}
                 <div className={`flex items-center h-16 px-3 flex-shrink-0 ${isSidebarCollapsed ? 'justify-center' : 'justify-between'}`}>
                    {!isSidebarCollapsed && <Logo collapsed={false} />}
                    <button
                        onClick={toggleSidebar}
                        className="text-gray-400 hover:text-white p-2 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        aria-label={isSidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
                    >
                        <FontAwesomeIcon icon={isSidebarCollapsed ? faChevronRight : faChevronLeft} className="h-4 w-4"/>
                    </button>
                </div>
                {/* Main Flexible Content Area */}
                <div className="flex-1 flex flex-col min-h-0 overflow-y-auto">
                    {/* Navigation Section */}
                    <nav className="mt-4 px-2 space-y-1">
                         <NavLink path="/dashboard" icon={faHouse} label="Dashboard" collapsed={isSidebarCollapsed} />
                         {/* --- START: Conditional Experimental Link (Setting + Email Check) --- */}
                         {showExperimentalFeaturesLink && (
                             <NavLink path="/experimental" icon={faFlask} label="Experimental" collapsed={isSidebarCollapsed} />
                         )}
                         {/* --- END: Conditional Experimental Link --- */}
                         <NavLink path="/analytics" icon={faChartSimple} label="Analytics" collapsed={isSidebarCollapsed} />
                         <NavLink path="/friends" icon={faUserGroup} label="Friends" collapsed={isSidebarCollapsed} />
                         <NavLink path="/journal" icon={faBook} label="Journal" collapsed={isSidebarCollapsed} />
                         {/* --- START: Conditional Custom Game Link (Emulator Mode) --- */}
                         {isEmulatorEnv && (
                            <NavLink path="/custom-game/setup" icon={faPuzzlePiece} label="Custom Game" collapsed={isSidebarCollapsed} />
                         )}
                         {/* --- END: Conditional Custom Game Link --- */}
                         <NavLink path="/testing" icon={faClipboardList} label="Assessments" collapsed={isSidebarCollapsed} />
                         {/* *** Add Install Button Section Conditionally *** */}
                         {canShowInstallOption && (
                             <>
                                 {/* Separator, only shown when not collapsed */}
                                 {!isSidebarCollapsed && <hr className="my-2 border-t border-gray-700" />}
                                 {/* Install Button */}
                                 <button
                                    onClick={handleSidebarInstallClick}
                                    className={`flex items-center w-full text-left px-3 py-2.5 rounded-md text-sm font-medium transition-colors duration-150 ease-in-out group text-indigo-300 hover:bg-gray-700 hover:text-white focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500 ${isSidebarCollapsed ? 'justify-center' : ''}`}
                                    title={isSidebarCollapsed ? installButtonLabel : undefined}
                                 >
                                     <FontAwesomeIcon icon={installButtonIcon} className={`${isSidebarCollapsed ? '' : 'mr-3'} h-5 w-5 flex-shrink-0 text-indigo-400 group-hover:text-white transition-colors duration-150 ease-in-out`} aria-hidden="true" />
                                     {!isSidebarCollapsed && <span className="whitespace-nowrap">{installButtonLabel}</span>}
                                 </button>
                             </>
                         )}
                         {/* Notification Button & Tray Wrapper */}
                          {/* ... (notification button code remains the same) ... */}
                         <div className="relative mt-1">
                             <button
                                 ref={notificationBellRef}
                                 onClick={toggleNotificationTray}
                                 className={`relative flex items-center w-full text-left px-3 py-2.5 rounded-md text-sm font-medium transition-colors duration-150 ease-in-out group focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500 ${
                                     isNotificationTrayOpen ? 'bg-gray-700 text-white' : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                                 } ${isSidebarCollapsed ? 'justify-center' : ''}`}
                                 title="Notifications"
                                 aria-label={`Notifications (${unreadCount} unread)`}
                                 aria-haspopup="true"
                                 aria-expanded={isNotificationTrayOpen}
                             >
                                 <div className="relative flex-shrink-0">
                                     <FontAwesomeIcon icon={faBell} className={`h-5 w-5 ${isSidebarCollapsed ? '' : 'mr-3'} text-gray-400 group-hover:text-white transition-colors duration-150 ease-in-out`} aria-hidden="true" />
                                     {!notificationsLoading && unreadCount > 0 && (
                                         <span className={`absolute -top-1.5 block h-4 w-4 rounded-full text-[10px] font-semibold pointer-events-none bg-red-500 text-white flex items-center justify-center border-2 border-gray-800 group-hover:border-gray-700 ${isSidebarCollapsed ? '-right-1' : '-right-1.5'}`}>
                                             {unreadCount > 9 ? '9+' : unreadCount}
                                         </span>
                                     )}
                                 </div>
                                 {!isSidebarCollapsed && <span className="whitespace-nowrap">Notifications</span>}
                             </button>
                             {/* Desktop Notification Tray Popover */}
                             {isNotificationTrayOpen && (
                                 <div
                                     ref={notificationTrayRef}
                                     onMouseDown={(e) => e.stopPropagation()}
                                     className={`fixed z-50 w-80 max-h-[calc(100vh-5rem)] overflow-y-auto rounded-lg shadow-xl bg-white ring-1 ring-black ring-opacity-5 focus:outline-none hidden lg:block ${isSidebarCollapsed ? 'left-16' : 'left-64'}`}
                                     style={{ top: typeof trayTopPosition === 'number' ? `${trayTopPosition}px` : undefined }}
                                     role="region"
                                     aria-label="Notifications Panel"
                                 >
                                     <NotificationTray navigate={navigate} onClose={() => setIsNotificationTrayOpen(false)} />
                                 </div>
                             )}
                         </div>
                    </nav>
                </div>
                {/* Sidebar Footer */}
                {/* ... (sidebar footer code remains the same) ... */}
                 <div className="px-2 py-4 border-t border-gray-700 space-y-1 flex-shrink-0">
                    <NavLink path="/settings" icon={faSliders} label="Profile Settings" collapsed={isSidebarCollapsed}/>
                    <NavLink path="/account" icon={faUserCog} label="Account" collapsed={isSidebarCollapsed}/>
                    <NavLink path="/profile-selection" icon={faUser} label="Change Profile" collapsed={isSidebarCollapsed}/>
                    <button
                        onClick={handleLogout}
                        className={`flex items-center w-full text-left px-3 py-2.5 rounded-md text-sm font-medium transition-colors duration-150 ease-in-out group text-red-300 hover:bg-red-700 hover:text-white focus:outline-none focus:ring-2 focus:ring-inset focus:ring-red-500 ${isSidebarCollapsed ? 'justify-center' : ''}`}
                        title={isSidebarCollapsed ? "Sign Out" : "Sign Out"}
                    >
                         <FontAwesomeIcon icon={faSignOutAlt} className={`${isSidebarCollapsed ? '' : 'mr-3'} h-5 w-5 flex-shrink-0 text-red-400 group-hover:text-white transition-colors duration-150 ease-in-out`} aria-hidden="true"/>
                        {!isSidebarCollapsed && <span className="whitespace-nowrap">Sign Out</span>}
                    </button>
                </div>
                <ProfileSection collapsed={isSidebarCollapsed} />
            </aside>

            {/* === Content Area Wrapper (Includes Mobile Header) === */}
            <div className="flex-1 flex flex-col relative min-h-0">

                {/* === Mobile Header Bar === */}
                <div
                    className={`lg:hidden bg-gray-800 w-full flex items-center justify-between px-4 shadow-md z-20 fixed top-0 left-0 right-0 h-16`} // KEEP h-16
                    style={mobileHeaderStyle} // Apply dynamic minHeight, paddingTop + boxSizing
                >
                     {/* Header Content (Logo, Buttons) should vertically center now */}
                     <Logo collapsed={true}/>
                     <div className="flex items-center space-x-2">
                          {/* No Install button in header bar directly */}
                          {authUser && (
                              <button
                                  ref={mobileNotificationBellRef}
                                  onClick={toggleNotificationTray}
                                  className="relative p-2 text-gray-300 hover:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 rounded-md"
                                  aria-label={`Notifications (${unreadCount} unread)`}
                                  aria-haspopup="true"
                                  aria-expanded={isNotificationTrayOpen}
                              >
                                   <div className="relative">
                                       <FontAwesomeIcon icon={faBell} className="h-5 w-5" />
                                       {!notificationsLoading && unreadCount > 0 && (
                                           <span className="absolute -top-1.5 -right-1.5 block h-4 w-4 rounded-full bg-red-500 text-white text-[10px] font-semibold flex items-center justify-center border-2 border-gray-800">
                                               {unreadCount > 9 ? '9+' : unreadCount}
                                           </span>
                                       )}
                                   </div>
                              </button>
                          )}
                          <button
                              onClick={toggleMobileMenu}
                              className="text-white p-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 rounded-md"
                              aria-label="Open main menu"
                              aria-expanded={isMobileMenuOpen}
                              aria-controls="mobile-menu"
                          >
                              <FontAwesomeIcon icon={faBars} className="h-5 w-5" />
                          </button>
                      </div>
                  </div>

                {/* === Main Content Outlet === */}
                 <main
                    className={`flex-1 overflow-y-auto bg-gray-100 p-4 sm:p-6 lg:p-8 lg:pt-8 pt-20 ios-touch-scroll`}
                    style={mainContentStyle} // Apply dynamic override paddingTop only if needed
                >
                    <Outlet /> {/* Where child route components render */}
                </main>

            </div> {/* End Content Area Wrapper */}


            {/* === Mobile Menu Overlay === */}
            {isMobileMenuOpen && (
                <div className="fixed inset-0 z-40 lg:hidden" role="dialog" aria-modal="true" id="mobile-menu">
                    <div className="fixed inset-0 bg-black bg-opacity-60 transition-opacity" aria-hidden="true" onClick={toggleMobileMenu}></div>
                    {/* APPLY INLINE STYLE TO OUTER CONTAINER */}
                    <div
                        className="fixed inset-y-0 left-0 max-w-xs w-full bg-gray-800 shadow-xl flex flex-col text-white z-50"
                        style={mobileMenuStyle} // Apply dynamic paddingTop here to push the whole menu down
                    >
                        {/* Menu's own header */}
                        {/* ... (mobile menu header remains the same) ... */}
                        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-700 h-16 flex-shrink-0">
                            <Logo collapsed={false} />
                            <button onClick={toggleMobileMenu} className="text-gray-300 hover:text-white p-2 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500" aria-label="Close menu">
                                <FontAwesomeIcon icon={faTimes} className="h-5 w-5" />
                            </button>
                        </div>

                        {/* Inner scrollable content */}
                        <div
                            className="flex-1 mt-4 overflow-y-auto"
                        >
                            {/* Navigation items */}
                            <nav className="px-2 py-4 space-y-1">
                                <NavLink path="/dashboard" icon={faHouse} label="Dashboard" collapsed={false} />
                                 {/* --- START: Conditional Experimental Link (Mobile - Setting + Email Check) --- */}
                                {showExperimentalFeaturesLink && (
                                    <NavLink path="/experimental" icon={faFlask} label="Experimental" collapsed={false} />
                                )}
                                {/* --- END: Conditional Experimental Link (Mobile) --- */}
                                <NavLink path="/analytics" icon={faChartSimple} label="Analytics" collapsed={false} />
                                <NavLink path="/friends" icon={faUserGroup} label="Friends" collapsed={false} />
                                <NavLink path="/journal" icon={faBook} label="Journal" collapsed={false} />
                                {/* --- START: Conditional Custom Game Link (Mobile - Emulator Mode) --- */}
                                {isEmulatorEnv && (
                                    <NavLink path="/custom-game/setup" icon={faPuzzlePiece} label="Custom Game" collapsed={false} />
                                )}
                                {/* --- END: Conditional Custom Game Link (Mobile) --- */}
                                <NavLink path="/testing" icon={faClipboardList} label="Assessments" collapsed={false} />
                                {/* *** Add Install Button Section Conditionally *** */}
                                {canShowInstallOption && (
                                    <>
                                        {/* Separator */}
                                        <hr className="my-2 border-t border-gray-700" />
                                        {/* Install Button */}
                                        <button
                                            onClick={handleSidebarInstallClick}
                                            className={`flex items-center w-full text-left px-3 py-2.5 rounded-md text-sm font-medium transition-colors duration-150 ease-in-out group text-indigo-300 hover:bg-gray-700 hover:text-white focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500`}
                                        >
                                            <FontAwesomeIcon icon={installButtonIcon} className={`mr-3 h-5 w-5 flex-shrink-0 text-indigo-400 group-hover:text-white transition-colors duration-150 ease-in-out`} aria-hidden="true" />
                                            <span className="whitespace-nowrap">{installButtonLabel}</span>
                                        </button>
                                    </>
                                )}
                            </nav>
                            {/* Rest of mobile menu sections */}
                             {/* ... (settings/account/profile links) ... */}
                             <div className="px-2 py-4 border-t border-gray-700 space-y-1">
                                <NavLink path="/settings" icon={faSliders} label="Profile Settings" collapsed={false}/>
                                <NavLink path="/account" icon={faUserCog} label="Account" collapsed={false}/>
                                <NavLink path="/profile-selection" icon={faUser} label="Change Profile" collapsed={false}/>
                            </div>
                        </div>
                        {/* Menu footers */}
                         {/* ... (profile section, logout button) ... */}
                         <ProfileSection collapsed={false} />
                        <div className="px-2 py-4 border-t border-gray-700 flex-shrink-0">
                            <button onClick={handleLogout} className="flex items-center w-full text-left px-3 py-2.5 rounded-md text-sm font-medium transition-colors duration-150 ease-in-out group text-red-300 hover:bg-red-700 hover:text-white focus:outline-none focus:ring-2 focus:ring-inset focus:ring-red-500">
                                <FontAwesomeIcon icon={faSignOutAlt} className="mr-3 h-5 w-5 flex-shrink-0 text-red-400 group-hover:text-white transition-colors duration-150 ease-in-out" aria-hidden="true"/>
                                <span className="whitespace-nowrap">Sign Out</span>
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* === Notification Tray for Mobile === */}
             {/* ... (notification tray rendering remains the same) ... */}
             {isNotificationTrayOpen && (
                <div
                    ref={notificationTrayRef}
                    onMouseDown={(e) => e.stopPropagation()}
                    className="fixed z-50 right-4 w-80 max-h-[calc(100vh-5rem)] overflow-y-auto rounded-lg shadow-xl bg-white ring-1 ring-black ring-opacity-5 focus:outline-none lg:hidden"
                    style={mobileNotificationStyle}
                    role="region"
                    aria-label="Notifications Panel"
                 >
                    <NotificationTray
                        navigate={navigate}
                        onClose={() => setIsNotificationTrayOpen(false)}
                    />
                </div>
            )}


            {/* === iOS Install Instructions Modal === */}
            {/* Render iOS modal conditionally based on local state */}
             <AddToHomeScreenModal isOpen={showIosInstallModal} onClose={handleCloseIosModal} />


            {/* === Floating Feedback Button === */}
            {authUser && <FeedbackButton />}

        </div> // End main container div
    );
};

export default SidebarLayout;